import { Button, type ColorTokens, Icon, type IconProps, Typography, token } from "@may-i/mash-design-system";
import * as RadixToast from "@radix-ui/react-toast";
import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import styled, { keyframes } from "styled-components";

type ToastType = "success" | "error" | "warning" | "info" | "default";

type ToastData = {
  title: string;
  content: string;
  type: ToastType;
  duration?: number;
};

type ToastContext = {
  showToast: (toast: ToastData) => void;
};

const ToastContext = createContext<ToastContext>({
  showToast: () => {},
});

// eslint-disable-next-line react-refresh/only-export-components
export function useToast() {
  return useContext(ToastContext);
}

export function ToastProvider({ children }: PropsWithChildren) {
  const [toasts, setToasts] = useState<(ToastData & { id: number })[]>([]);

  const showToast = useCallback((toast: ToastData) => {
    setToasts((prev) => [...prev, { ...toast, id: Date.now() }]);
  }, []);

  const closeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <RadixToast.Provider swipeDirection="down">
      <ToastContext.Provider value={{ showToast }}>
        {children}
        {toasts.map((toast) => (
          <ToastItem key={toast.id} {...toast} onClose={() => closeToast(toast.id)} />
        ))}
        <ToastViewport />
      </ToastContext.Provider>
    </RadixToast.Provider>
  );
}

// --- Toast Item ---

function getIconInfo(type: ToastType): { icon: IconProps["icon"]; color: ColorTokens } | null {
  switch (type) {
    case "success":
      return { icon: "ic_success", color: "colors.semantics.success.main" };
    case "error":
      return { icon: "ic_error", color: "colors.semantics.error.main" };
    case "warning":
      return { icon: "ic_warning", color: "colors.semantics.warning.main" };
    case "info":
      return { icon: "ic_information", color: "colors.semantics.info.main" };
    default:
      return null;
  }
}

function ToastItem({
  type,
  title,
  content,
  duration = 3000,
  onClose,
}: ToastData & { onClose: () => void }) {
  const [open, setOpen] = useState(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const iconInfo = getIconInfo(type);

  useEffect(() => {
    if (open) {
      timerRef.current = setTimeout(() => setOpen(false), duration);
    } else {
      timerRef.current = setTimeout(() => onClose(), 300);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [open, duration, onClose]);

  return (
    <ToastRoot $type={type} type="foreground" open={open} onOpenChange={setOpen}>
      {iconInfo && <StyledIcon icon={iconInfo.icon} $color={iconInfo.color} />}
      <ContentWrapper>
        <Title $type={type}>{title}</Title>
        <Description $type={type}>{content}</Description>
      </ContentWrapper>
      <RadixToast.Close asChild>
        <CloseButton>
          <Icon icon="ic_close" />
        </CloseButton>
      </RadixToast.Close>
    </ToastRoot>
  );
}

// --- Styles ---

const slideIn = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

const slideOut = keyframes`
  from { transform: translateY(0); }
  to { transform: translateY(100%); }
`;

const ToastViewport = styled(RadixToast.Viewport)`
  position: fixed;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  z-index: 9999;
`;

const ToastRoot = styled(RadixToast.Root)<{ $type: ToastType }>`
  background-color: ${({ $type }) =>
    $type === "default" ? token("colors.gray.black") : token("colors.gray.white")};
  border-radius: 8px;
  box-shadow: 0px 8px 16px 0px rgba(105, 111, 140, 0.16);
  border-left: 8px solid
    ${({ $type }) => {
      switch ($type) {
        case "success":
          return token("colors.semantics.success.main");
        case "error":
          return token("colors.semantics.error.main");
        case "warning":
          return token("colors.semantics.warning.main");
        case "info":
          return token("colors.semantics.info.main");
        default:
          return token("colors.gray.black");
      }
    }};
  display: flex;
  align-items: center;
  gap: 20px;
  width: 448px;
  box-sizing: border-box;
  padding: 17px 0 17px 20px;

  &[data-state="open"] {
    animation: ${slideIn} 150ms ease-out;
  }
  &[data-state="closed"] {
    animation: ${slideOut} 150ms ease-in;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Title = styled(Typography).attrs({ typo: "subheading1" })<{ $type: ToastType }>`
  color: ${({ $type }) =>
    $type === "default" ? token("colors.gray.white") : token("colors.gray.black")};
`;

const Description = styled(Typography).attrs({ typo: "caption" })<{ $type: ToastType }>`
  color: ${({ $type }) =>
    $type === "default" ? token("colors.gray.GR400") : token("colors.gray.GR700")};
  /* 저장 실패 시 서버가 준 사유를 다음 줄에 붙여 보여준다(FR-CAM-018).
     줄바꿈이 없는 기존 문구는 영향받지 않는다. */
  white-space: pre-line;
`;

const CloseButton = styled(Button).attrs({ variant: "text" })`
  padding: 8px;
  margin: 8px;
  > svg {
    color: ${token("colors.gray.GR900")};
    width: 16px;
  }
`;

const StyledIcon = styled(Icon)<{ $color: ColorTokens }>`
  width: 40px;
  color: ${({ $color }) => token($color)};
`;
